import { useEffect, useMemo, useState } from "react";
import Faqs from "../../components/Faqs";
import LetsGetInTouch from "../../components/LetsGetInTouch";
import { usePersistStore } from "../../store";
import HowItWorks from "../../components/HowItWorks";
import { DASHBOARD_TYPE } from "../../utils/enums";

type QuickLink = {
  icon: string;
  title: string;
  description: string;
  anchor: string;
};

const QUICK_LINKS: QuickLink[] = [
  {
    icon: "bi-compass",
    title: "How it works",
    description: "A walkthrough of the closed-bid flow.",
    anchor: "HowItWorks",
  },
  {
    icon: "bi-question-circle",
    title: "Browse FAQs",
    description: "Quick answers to common questions.",
    anchor: "Faqs",
  },
  {
    icon: "bi-chat-dots",
    title: "Contact support",
    description: "Send us a message — we usually reply same-day.",
    anchor: "ContactUs",
  },
];

function HelpAndSupport() {
  const { dashboardType } = usePersistStore((s: any) => s);
  const [activeTab, setActiveTab] = useState(dashboardType);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    setActiveTab(dashboardType);
  }, [dashboardType]);

  const handleQuickLink = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const resultHint = useMemo(() => {
    const trimmed = keyword.trim();
    if (!trimmed) return null;
    return `Showing matches for "${trimmed}"`;
  }, [keyword]);

  return (
    <div className="bg-pink p-4 dashHelp">
      <header className="dashHelp__header">
        <div>
          <span className="dashHelp__kicker">
            <i className="bi bi-life-preserver" aria-hidden="true" />
            Help center
          </span>
          <h1 className="dashHelp__title">How can we help?</h1>
          <p className="dashHelp__lead">
            Walk through how Boll-e&trade; works, browse the most common questions, or
            send us a note &mdash; we'll usually reply within a business day.
          </p>
        </div>
      </header>

      <div className="dashHelp__quickLinks">
        {QUICK_LINKS.map((link) => (
          <button
            key={link.title}
            type="button"
            className="dashHelp__quick"
            onClick={() => handleQuickLink(link.anchor)}
          >
            <span className="dashHelp__quickIcon">
              <i className={`bi ${link.icon}`} aria-hidden="true" />
            </span>
            <span className="dashHelp__quickBody">
              <span className="dashHelp__quickTitle">{link.title}</span>
              <span className="dashHelp__quickDesc">{link.description}</span>
            </span>
            <i
              className="bi bi-arrow-up-right dashHelp__quickArrow"
              aria-hidden="true"
            />
          </button>
        ))}
      </div>

      <section id="HowItWorks" className="dashHelp__section">
        <HowItWorks showSwitch={false} />
      </section>

      <section id="Faqs" className="dashHelp__section dashHelp__faqs">
        <div className="dashHelp__sectionHeader">
          <span className="dashHelp__sectionKicker">FAQs</span>
          <h2>Frequently asked questions</h2>
          <p>
            Find answers to common questions about buying and selling on Boll-e&trade;
            &mdash; payments, listings, shipping, and more. Don't see what
            you're after? Drop us a message below.
          </p>
        </div>

        <div className="dashHelp__faqsControls">
          <div
            className="dashModeSwitch"
            role="tablist"
            aria-label="FAQ audience"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === DASHBOARD_TYPE.BUYER}
              className={`dashModeSwitch__opt ${
                activeTab === DASHBOARD_TYPE.BUYER ? "is-active" : ""
              }`}
              onClick={() => setActiveTab(DASHBOARD_TYPE.BUYER)}
            >
              Buyer
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === DASHBOARD_TYPE.SELLER}
              className={`dashModeSwitch__opt ${
                activeTab === DASHBOARD_TYPE.SELLER ? "is-active" : ""
              }`}
              onClick={() => setActiveTab(DASHBOARD_TYPE.SELLER)}
            >
              Seller
            </button>
          </div>

          <div className="dashHelp__faqsSearch">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              type="search"
              placeholder="Filter questions…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            {keyword && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setKeyword("")}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {resultHint && (
          <p className="dashHelp__resultHint">{resultHint}</p>
        )}

        <div className="dashHelp__faqsContent">
          <Faqs type={activeTab} keyword={keyword} />
        </div>
      </section>

      <section id="ContactUs" className="dashHelp__section">
        <LetsGetInTouch />
      </section>
    </div>
  );
}

export default HelpAndSupport;
